import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase.js';

export function useCollection<T = Record<string, unknown>>(
  path: string,
  constraints: QueryConstraint[] = [],
): { docs: Array<T & { id: string }>; loading: boolean; error: Error | null } {
  const [docs, setDocs] = useState<Array<T & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stringify constraints for dependency tracking. Pass stable arrays.
  const key = JSON.stringify(constraints.map((c) => c.toString()));

  useEffect(() => {
    const q = query(collection(db, path), ...constraints);
    return onSnapshot(
      q,
      (snap) => {
        setDocs(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);

  return { docs, loading, error };
}

export function useDoc<T = Record<string, unknown>>(
  path: string,
  id: string | undefined,
): { data: (T & { id: string }) | null; loading: boolean } {
  const [data, setData] = useState<(T & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }
    return onSnapshot(doc(db, path, id), (snap) => {
      if (snap.exists()) {
        setData({ id: snap.id, ...(snap.data() as T) });
      } else {
        setData(null);
      }
      setLoading(false);
    });
  }, [path, id]);

  return { data, loading };
}

// Re-export firestore helpers callers reach for
export { orderBy, where, limit };
